import { InfographState } from 'types/infographTypes';
import request from 'utils/request';

type SaveGeneratedDesignResponse = { status: string; designId: string };

const BASE_PATH = '/multipage/infographic';

/**
 * Save generated design to user's account
 *
 * @param generatedInfographState
 * @returns {SaveGeneratedDesignResponse}
 */
export const saveGeneratedDesign = async (generatedInfographState: InfographState, e1TemplateId: string) => {
  return await request<SaveGeneratedDesignResponse>(`${BASE_PATH}/save_generated_design`, {
    method: 'POST',
    body: JSON.stringify({
      infographState: generatedInfographState,
      source_type: 'e2-template',
      source_id: e1TemplateId,
    }),
  });
};

// This is out of scope for now, keep as a reference
type InfographParentTableResponse = {
  [key in string]: null | string;
};

export type CreateE1SuccessResponse = {
  status: 'ok';
  infographic:
    | string // the new infograph id, created from the template
    | {
        count: 0;
        data: InfographParentTableResponse;
      };
  storage: null;
};

export type CreateE1FailResponse = {
  status: 'fail';
  message: string;
};

export type CreateE1OverlimitResponse = {
  status: 'overlimit';
};

type CreateE1Response = CreateE1SuccessResponse | CreateE1FailResponse | CreateE1OverlimitResponse;

export class OverLimitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OverLimitError';
  }
}

/**
 * A end-point to create an E1 design using a template with the given id
 *
 * @param {string} sourceId the template id
 * @returns
 */
export const createE1Design = (sourceId: string) => {
  return new Promise<CreateE1SuccessResponse>((resolve, reject) => {
    if (!sourceId) {
      reject(new Error('The E1 template id is invalid'));
      return;
    }

    fetch(`${BASE_PATH}/create`, {
      method: 'POST',
      body: JSON.stringify({
        folder: null,
        source_id: sourceId,
        source_type: 'template',
        mixpanel_data: {
          title: 'E2 Go Classic',
          filters: [],
          ishr: false,
          redirectFromSearch: false,
          isRecurringRecommendation: false,
        },
      }),
      headers: {
        Accept: 'application/json',
        'Content-type': 'application/json',
      },
    })
      .then((httpResponse) => {
        if (!httpResponse.ok) {
          throw new Error(`create E1 infograph fail with error code: ${httpResponse.status}`);
        }

        return httpResponse.json() as Promise<CreateE1Response>;
      })
      .then((response) => {
        if (response.status === 'overlimit') {
          throw new OverLimitError(`create E1 infograph fail because it is ${response.status}`);
        }

        if (response.status === 'fail') {
          throw new Error(response.message ?? 'create E1 infograph fail');
        }

        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

/////////////// CREATE_E2 ENDPOINT ///////////////
export type CreateE2DesignRequest = {
  // If TRUE, the new design will appear beside the source design
  copyToSide?: boolean;

  // Mixpanel event to trigger upon creation
  mpEvent?: string;

  // Folder to save the design to
  // Empty string for root folder
  folder?: string;

  // If TRUE, will copy the modified_at field from the source infographic to the new one
  fullData?: boolean;

  // Title of new design
  copyTitle?: string;

  // Infograph UUID or template id to copy from
  sourceId: string;
  sourceType: 'e2-infographic' | 'e2-template';
};

// `infographic` is an object with a `data` field when copying e2 infographic
// `infographic` is a string when creating from e2 template
// Note: there are other fields in the infographic response object, but only need the id for now
type CreateE2DesignResponse = { status: string; infographic: { data: { id: string } } | string };

/**
 * Create an E2 design from an existing E2 design or template
 * @param params
 * @returns {CreateE2DesignResponse}
 */
export const createE2Design = async (params: CreateE2DesignRequest) => {
  const { copyToSide, mpEvent, folder, fullData, sourceId, sourceType, copyTitle } = params;
  const requestBody = {
    event: mpEvent,
    full_data: fullData,
    source_id: sourceId,
    source_type: sourceType,
    copyTitle,
    folder,
    copyToSide,
  };

  return await request<CreateE2DesignResponse>(`${BASE_PATH}/create_e2`, {
    method: 'POST',
    body: JSON.stringify(requestBody),
  });
};

type CheckUserOverLimitResponse = {
  status: 'ok' | 'fail';
  overlimit: boolean;
};
/**
 * Update design by prompt using E1 backend API
 * Note: must use E1 backend API so it uses the correct JWT token
 *
 * @param designId to update
 * @param prompt to update by
 * @returns {UpdateDesignByPromptResponse}
 */
export const checkUserOverLimit = async () =>
  await request<CheckUserOverLimitResponse>(`${BASE_PATH}/user_overlimit`, {
    method: 'GET',
  });
